/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { Column } from "../../../../../common/commonComponents/lotDetailsTable";
import {
	Container,
	DispenseState,
} from "../../../types/mediaBatchDispense.types";

const getDispenseColumns = (
	t: (key: string) => string, // Pass the translation function in
	nominalValue: number,
	dispenseData: DispenseState,
	onCheck: (id: string, checked: boolean) => void,
	onActualChange: (id: string, actual: number) => void,
	isLimitReached: boolean,
): Column[] => [
	{
		header: "",
		key: "container_id",
		render: (row: Container) => {
			const isChecked = dispenseData[row.container_id]?.checked || false;
			return (
				<input
					type="checkbox"
					className="rounded-md"
					checked={isChecked}
					disabled={!isChecked && isLimitReached}
					onChange={(e) => onCheck(row.container_id, e.target.checked)}
				/>
			);
		},
	},
	{
		header: t("mediaBatchInit.dispenseEntry.dispenseTable.serialNumber"), // Fixed your duplicate header bug!
		key: "serial_number",
		render: (row: Container) => (
			<span className="text-[11px] font-black text-slate-700 whitespace-nowrap">
				{row.serial_number}
			</span>
		),
	},
	{
		header: t("mediaBatchInit.dispenseEntry.dispenseTable.capacity"),
		key: "nominal",
		render: () => (
			<span className="text-[11px] font-black text-slate-700">
				{nominalValue}
			</span>
		),
	},
	{
		header: t("mediaBatchInit.dispenseEntry.dispenseTable.actualQuantity"),
		key: "actual",
		render: (row: Container) => {
			const actualValue =
				dispenseData[row.container_id]?.actual ?? nominalValue;
			return (
				<input
					type="number"
					value={actualValue}
					min={0}
					onChange={(e) =>
						onActualChange(row.container_id, Number(e.target.value))
					}
					className="w-24 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-orange-100 focus:border-orange-500 transition-all"
				/>
			);
		},
	},
	{
		header: t("mediaBatchInit.dispenseEntry.dispenseTable.variance"),
		key: "variance",
		render: (row: Container) => {
			const actualValue =
				dispenseData[row.container_id]?.actual ?? nominalValue;
			const variance = actualValue - nominalValue;
			return (
				<span
					className={`text-[11px] font-black ${
						variance === 0
							? "text-emerald-600"
							: variance > 0
								? "text-blue-600"
								: "text-red-600"
					}`}
				>
					{variance > 0 ? `+${variance}` : variance}
				</span>
			);
		},
	},
];

export const getDispenseHistoryColumns = (t: (key: string) => string): Column[] => [
	{
		header: t("mediaBatchInit.dispenseEntry.historyTable.dateTime"),
		key: "",
		className: "font-bold text-slate-500 whitespace-nowrap",
		render: () => null,
	},
	{
		header: t("mediaBatchInit.dispenseEntry.historyTable.serialNo"),
		key: "",
		className:
			"font-black text-blue-600 underline underline-offset-4 cursor-pointer",
		render: () => null,
	},
	{
		header: t("mediaBatchInit.dispenseEntry.historyTable.mediaBatch"),
		key: "",
		className: "font-bold text-slate-800",
		render: () => null,
	},
	{
		header: t("mediaBatchInit.dispenseEntry.historyTable.containerBatch"),
		key: "",
		className: "font-bold text-slate-800",
		render: () => null,
	},
	{
		header: t("mediaBatchInit.dispenseEntry.historyTable.lot"),
		key: "",
		className: "font-bold text-slate-800",
		render: () => null,
	},
	{
		header: t("mediaBatchInit.dispenseEntry.historyTable.varianceObservation"),
		key: "",
		className: "font-black text-[#0F172A] text-center",
		render: () => null,
	},
	{
		header: t("mediaBatchInit.dispenseEntry.historyTable.remainingQty"),
		key: "",
		className: "font-black text-[#0F172A] text-center",
		render: () => null,
	},
	{
		header: t("mediaBatchInit.dispenseEntry.historyTable.uom"),
		key: "",
		className: "font-black text-slate-400 uppercase tracking-widest",
		render: () => null,
	},
	{
		header: t("mediaBatchInit.dispenseEntry.historyTable.dispensedBy"),
		key: "",
		className: "font-bold text-slate-700 whitespace-nowrap",
		render: () => null,
	},
	{
		header: t("mediaBatchInit.dispenseEntry.historyTable.workstation"),
		key: "",
		className: "font-bold text-slate-700",
		render: () => null,
	},
	{
		header: t("mediaBatchInit.dispenseEntry.historyTable.status"),
		key: "",
		className: "",
		render: () => (
			<span className="flex items-center gap-1.5 text-emerald-500 text-[9px] font-black uppercase tracking-widest">
				<div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/30"></div>
				{t("mediaBatchInit.historyTable.completed")}
			</span>
		),
	},
	{
		header: t("mediaBatchInit.dispenseEntry.historyTable.remarks"),
		key: "",
		className: "text-[10px] font-bold text-slate-400 italic truncate max-w-[150px]",
		render: () => null,
	},
];

export default getDispenseColumns;
